// Admin sidebar (ADM-UX-1, Part B).
//
// Desktop: a fixed rail that collapses to an icon-only width (labels hidden,
// tooltips + accessible names retained). Mobile keeps the existing Chakra
// Drawer, where labels stay visible and selecting a route closes it.
//
// The active item is always derived from the current route -- never from a
// click index -- so direct URLs, refreshes and back/forward navigation always
// highlight the correct entry.
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Icon,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { type IconType } from "react-icons";
import {
  HiOutlineChevronDoubleLeft,
  HiOutlineChevronDoubleRight,
  HiOutlineCreditCard,
  HiOutlineDocumentText,
  HiOutlineGlobe,
  HiOutlineHome,
  HiOutlineMusicNote,
  HiOutlineStatusOnline,
  HiOutlineTag,
  HiOutlineTrendingUp,
} from "react-icons/hi";
import { useLocation, useNavigate } from "react-router-dom";
import { PROTECTED_PATHS } from "routes/pagePath";

export const ADMIN_SIDEBAR_COLLAPSED_STORAGE_KEY = "choirscript-admin-sidebar-collapsed";

// Single source of truth for the desktop rail widths: the layout grid and the
// sidebar itself must never disagree about the collapsed/expanded sizes.
export const ADMIN_SIDEBAR_WIDTHS = {
  expanded: "220px",
  collapsed: "72px",
} as const;

export interface AdminNavItem {
  label: string;
  path: string;
  icon: IconType;
}

export const ADMIN_NAV_ITEMS: AdminNavItem[] = [
  { label: "Home", path: PROTECTED_PATHS.DASHBOARD, icon: HiOutlineHome },
  { label: "Scripts", path: PROTECTED_PATHS.UPLOAD_ADMIN, icon: HiOutlineDocumentText },
  { label: "Composers", path: PROTECTED_PATHS.COMPOSER, icon: HiOutlineMusicNote },
  { label: "Categories", path: PROTECTED_PATHS.CATEGORIES, icon: HiOutlineTag },
  { label: "Languages", path: PROTECTED_PATHS.LANGUAGES, icon: HiOutlineGlobe },
  { label: "Payment Ops", path: PROTECTED_PATHS.PAYMENT_OPERATIONS, icon: HiOutlineCreditCard },
  { label: "Product Health", path: PROTECTED_PATHS.PRODUCT_HEALTH, icon: HiOutlineStatusOnline },
  {
    label: "Growth Intelligence",
    path: PROTECTED_PATHS.GROWTH_INTELLIGENCE,
    icon: HiOutlineTrendingUp,
  },
];

// Exact match, or a child route (e.g. /composer/:id keeps "Composers" active).
export const isAdminNavItemActive = (pathname: string, itemPath: string): boolean =>
  pathname === itemPath || pathname.startsWith(`${itemPath}/`);

// Only the explicit "true" marker collapses the rail: a missing or invalid
// stored value falls back to the expanded navigation, and browsers where
// storage is unavailable simply keep the in-memory preference.
export const readStoredSidebarCollapsed = (): boolean => {
  try {
    return window.localStorage.getItem(ADMIN_SIDEBAR_COLLAPSED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

export const persistSidebarCollapsed = (isCollapsed: boolean): void => {
  try {
    window.localStorage.setItem(
      ADMIN_SIDEBAR_COLLAPSED_STORAGE_KEY,
      isCollapsed ? "true" : "false",
    );
  } catch {
    // Storage unavailable (private mode): keep the in-memory preference only.
  }
};

const ACTIVE_BG = "primary";
const ACTIVE_HOVER_BG = "#1a63a8";
// Non-colour active cue: in the collapsed rail the label is hidden, so the
// selected item also carries a shape marker (plus aria-current for AT).
const ACTIVE_MARKER_SHADOW = "inset 3px 0 0 0 rgba(255, 255, 255, 0.65)";
const INACTIVE_TEXT = "#2b3b4c";
const HOVER_BG = "#EDF2F7";
const RAIL_BORDER = "#E2E8F0";

interface SidebarNavLinksProps {
  isCollapsed: boolean;
  onNavigate?: () => void;
}

const SidebarNavLinks = ({ isCollapsed, onNavigate }: SidebarNavLinksProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  return (
    <VStack align="stretch" spacing={1} w="100%">
      {ADMIN_NAV_ITEMS.map((item) => {
        const isActive = isAdminNavItemActive(pathname, item.path);
        return (
          <Tooltip
            key={item.path}
            label={item.label}
            placement="right"
            hasArrow
            isDisabled={!isCollapsed}
          >
            <Button
              onClick={() => {
                navigate(item.path);
                onNavigate?.();
              }}
              aria-label={isCollapsed ? item.label : undefined}
              aria-current={isActive ? "page" : undefined}
              justifyContent={isCollapsed ? "center" : "flex-start"}
              px={isCollapsed ? 0 : 3}
              h="44px"
              w="100%"
              variant="ghost"
              borderRadius="md"
              bg={isActive ? ACTIVE_BG : "transparent"}
              color={isActive ? "white" : INACTIVE_TEXT}
              boxShadow={isActive ? ACTIVE_MARKER_SHADOW : undefined}
              fontWeight={isActive ? "semibold" : "medium"}
              fontSize="sm"
              _hover={{ bg: isActive ? ACTIVE_HOVER_BG : HOVER_BG }}
            >
              <Icon as={item.icon} boxSize={5} mr={isCollapsed ? 0 : 3} />
              {isCollapsed ? null : <Text as="span">{item.label}</Text>}
            </Button>
          </Tooltip>
        );
      })}
    </VStack>
  );
};

export interface SideBarProps {
  /** Mobile drawer visibility, opened from the NavBar hamburger. */
  isOpen: boolean;
  onClose: () => void;
  isCollapsed?: boolean;
  onToggleCollapsed?: () => void;
}

const Sidebar = ({ isOpen, onClose, isCollapsed = false, onToggleCollapsed }: SideBarProps) => {
  return (
    <>
      {/* Desktop rail: the authenticated shell hides it below md and the layout
          grid reserves its width. */}
      <Box
        as="nav"
        aria-label="Admin navigation"
        position="fixed"
        left={0}
        top={0}
        h="100%"
        w={isCollapsed ? ADMIN_SIDEBAR_WIDTHS.collapsed : ADMIN_SIDEBAR_WIDTHS.expanded}
        bg="white"
        borderRightWidth="1px"
        borderColor={RAIL_BORDER}
        boxShadow="sm"
        transition="width 150ms ease"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <Box mt="64px" px={isCollapsed ? 2 : 3} pb={2} flex="1" overflowY="auto">
          {/* Desktop navigation never needs to close a drawer. */}
          <SidebarNavLinks isCollapsed={isCollapsed} />
        </Box>

        {onToggleCollapsed === undefined ? null : (
          <Box p={2} borderTopWidth="1px" borderColor={RAIL_BORDER}>
            <Tooltip
              label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              placement="right"
              hasArrow
              isDisabled={!isCollapsed}
            >
              <Button
                onClick={onToggleCollapsed}
                aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                aria-expanded={!isCollapsed}
                justifyContent={isCollapsed ? "center" : "flex-start"}
                px={isCollapsed ? 0 : 3}
                h="40px"
                w="100%"
                variant="ghost"
                color={INACTIVE_TEXT}
                fontWeight="medium"
                fontSize="sm"
                _hover={{ bg: HOVER_BG }}
              >
                <Icon
                  as={isCollapsed ? HiOutlineChevronDoubleRight : HiOutlineChevronDoubleLeft}
                  boxSize={4}
                  mr={isCollapsed ? 0 : 2}
                />
                {isCollapsed ? null : <Text as="span">Collapse</Text>}
              </Button>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Mobile: the same ADMIN_NAV_ITEMS in a Drawer (a portal, so it opens
          even though the rail above is hidden below md). Selecting a route
          closes it. */}
      <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
        <DrawerOverlay>
          <DrawerContent>
            <DrawerCloseButton />
            <DrawerHeader>ChoirScript</DrawerHeader>
            <DrawerBody>
              <SidebarNavLinks isCollapsed={false} onNavigate={onClose} />
            </DrawerBody>
          </DrawerContent>
        </DrawerOverlay>
      </Drawer>
    </>
  );
};

export default Sidebar;
